import { useEffect, useContext, useState } from 'react'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined'
import { useFirestoreGeneralOrderBy } from '../../../firebase/useFirestore'
import { db, timestamp } from '../../../firebase/config'
import { v4 as uuid } from 'uuid'
import { client } from '../../../hooks/Client'
import { SavedIcon } from '../../../StateManagment/SavedIcon'
import DatalistOptions from './DatalistOptions'
import { STANDARDIZED_DATA_FIELDS } from '../../../helpers/standardizedDataFields'
import Table from '../../common/table/Table.jsx'
import TableCell from '../../common/table/TableCell.jsx'
import TableDragHandleCell from '../../common/table/TableDragHandleCell.jsx'
import DeleteModal from '../../common/DeleteModal.jsx'
import Modal from '../../common/Modal'
import Tooltip from '../../common/Tooltip'
import { DATA_TITLE_TOOLTIP_DELAY } from '../../../helpers/tooltip/tooltipDelays'
import ReadOnlyGuard from '../../common/ReadOnlyGuard'
import softDeleteDoc from '../../../helpers/softDeleteDoc';
import DataFieldValue from '../../common/dataFields/DataFieldValue'
import DataFieldTypeAndOptions from '../../common/dataFields/DataFieldTypeAndOptions'
import { resolveDataFieldType } from '../../../helpers/dataFields/dataFieldTypes'
import useDataFieldOptions from '../../../hooks/useDataFieldOptions'
import uploadDataFieldFile from '../../../helpers/dataFields/uploadDataFieldFile'
import { isFixedFieldTitle } from '../../../helpers/adressHelpers/householdFields'

/**
 * De bewerkbare gegevenstabel van één huishouden (PersonaData op het adres).
 *
 * Losgetrokken uit het tabblad 'Gegevens' zodat de afspraak/taak dezelfde tabel
 * toont in plaats van een tweede kopie ervan.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * Elk gegeven heeft een veldtype, in dezelfde vorm als op het intakeformulier.
 * Komt de regel uit zo'n formulier, dan staat het type er al op; wordt hij hier
 * toegevoegd, dan kiest de gebruiker het zelf.
 * AI-CONTEXT: See docs/features/gegevensveld-types.md
 *
 * Vaste gegevens (naam, contact, adres, woning) staan hier NIET: die toont de
 * kaart 'Vaste gegevens'. Oude regels met zo'n titel worden verborgen tot de
 * migratie ze opruimt, en een eigen gegeven kan zo'n titel niet krijgen.
 * AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T4)
 *
 * @param {object} props
 * @param {string} props.adressID - Adresses.ID van het huishouden (niet de docid)
 */
const HouseholdDataTable = ({ adressID }) => {
  // Context
  const [, setSaved] = useContext(SavedIcon)

  // State
  const [dragArrayData, setDragArrayData] = useState([])
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteDocid, setDeleteDocid] = useState('')
  // Alleen de docid, niet het document zelf: het bewerkvenster toont de soort
  // én — afhankelijk daarvan — de antwoordopties, dus het moet de regel volgen
  // zoals hij NU is. Een bij het klikken vastgelegde momentopname zou blijven
  // hangen op de oude soort, en dan verschijnen de opties niet.
  // AI-CONTEXT: See docs/pitfalls.md#state-click-captured-firestore-snapshot
  const [editDocid, setEditDocid] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [titleError, setTitleError] = useState('')

  // Firestore
  const data = useFirestoreGeneralOrderBy('PersonaData', 'PersonaID', adressID || '', 'Position', 'asc')
  const { optionsFor, optionKey } = useDataFieldOptions()

  /** De regel die op dit moment bewerkt wordt, altijd uit de verse lijst. */
  const editItem = (data || []).find((item) => item.docid === editDocid) || null

  // Whenever Firestore data changes, update dragArray state
  useEffect(() => {
    setDragArrayData((data || []).filter((item) => !isFixedFieldTitle(item.Title)))
  }, [data])

  // Handle Drag & Drop reordering
  const handleDragEndData = (result) => {
    if (!result.destination) return

    const items = Array.from(dragArrayData)
    const [movedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, movedItem)

    // Update local state to reflect the reorder immediately
    setDragArrayData(items)

    // Now update Firestore with new positions
    items.forEach((item, idx) => {
      db.collection('PersonaData')
        .doc(item.docid)
        .update({ Position: idx + 1 })
    })
  }

  const titleHandler = (e) => {
    const docid = e.target.dataset.docid
    const title = e.target.value

    if (isFixedFieldTitle(title)) {
      setTitleError(`'${title.trim()}' is een vast gegeven. Vul het in bij Vaste gegevens.`)
      // Terug naar wat er stond: het veld is ongestuurd, dus anders blijft de
      // geweigerde titel staan alsof hij is opgeslagen.
      e.target.value = (data || []).find((item) => item.docid === docid)?.Title || ''
      return
    }
    setTitleError('')

    db.collection('PersonaData')
      .doc(docid)
      .update({ Title: title })
      .then(() => setSaved('flex'))
  }

  const valueHandler = (docid, value) => {
    db.collection('PersonaData')
      .doc(docid)
      .update({ Value: value })
      .then(() => setSaved('flex'))
  }

  const fieldTypeHandler = (fieldType) => {
    if (!editDocid) return

    db.collection('PersonaData')
      .doc(editDocid)
      .update({ FieldType: fieldType })
      .then(() => setSaved('flex'))
  }

  const uploadHandler = async (docid, file) => {
    setUploadError('')

    try {
      const url = await uploadDataFieldFile({ file, ownerId: adressID })
      await db.collection('PersonaData').doc(docid).update({ Value: url })
      setSaved('flex')
    } catch (err) {
      // Laten zien in plaats van wegslikken: een mislukte upload die eruitziet
      // als een gelukte kost de gebruiker het document.
      console.error('Uploaden van het gegeven mislukt:', err)
      setUploadError('Het bestand kon niet worden geüpload. Probeer het opnieuw.')
    }
  }

  const deleteNAW = (e) => {
    const docid = e.currentTarget.dataset.docid

    softDeleteDoc({ collection: 'PersonaData', docid })
      .then(() => setSaved('flex'))
  }

  const openDeleteModal = (e) => {
    setDeleteDocid(e.currentTarget.dataset.docid)
    setDeleteModal(true)
  }

  const deleteStandardizedField = () => {
    softDeleteDoc({ collection: 'PersonaData', docid: deleteDocid })
      .then(() => setSaved('flex'))
  }

  const addData = () => {
    db.collection('PersonaData')
      .add({
        ID: uuid(),
        PersonaID: adressID,
        Title: '',
        Value: '',
        FieldType: '',
        CompagnyID: client,
        Compagny: client,
        Position: data.length + 1,
        Timestamp: timestamp
      })
      .then(() => setSaved('flex'))
  }

  // --- Antwoordopties van een meerkeuzeveld ---
  // De sleutel is het formulierveld als de regel uit een intakeformulier komt,
  // en anders de regel zelf. Zo delen alle antwoorden op dezelfde vraag hun
  // opties, precies zoals bij het formulier.
  const addOption = () => {
    if (!editItem) return

    db.collection('MultipleQuestionOptions').add({
      ID: uuid(),
      Option: '',
      Question: editItem.Title || '',
      Field: optionKey(editItem),
      CompagnyID: client,
      Compagny: client,
      Position: optionsFor(editItem).length + 1,
      Timestamp: timestamp,
    }).then(() => setSaved('flex'))
  }

  const changeOption = (docid, value) => {
    db.collection('MultipleQuestionOptions').doc(docid).update({ Option: value })
      .then(() => setSaved('flex'))
  }

  const deleteOption = (docid) => {
    softDeleteDoc({ collection: 'MultipleQuestionOptions', docid })
      .then(() => setSaved('flex'))
  }

  const reorderOptions = (items) => {
    items.forEach((optie, idx) => {
      db.collection('MultipleQuestionOptions').doc(optie.docid).update({ Position: idx + 1 })
    })
    setSaved('flex')
  }

  return (
    <>
      <datalist id="form-field-item-titles">
        <DatalistOptions />
      </datalist>
      {uploadError && <p className="data-field-error" role="alert">{uploadError}</p>}
      {titleError && <p className="data-field-error" role="alert">{titleError}</p>}
      <ReadOnlyGuard edit="addresses.edit">
        {(canEdit) => (
          <Table
            columns={[
              { key: 'drag', label: '', searchable: false },
              { key: 'title', label: 'TITEL', headerClassName: 'common-table__title-column' },
              { key: 'value', label: 'GEGEVEN', searchable: false },
              { key: 'edit', label: 'AANPASSEN', searchable: false },
              { key: 'delete', label: 'VERWIJDEREN', searchable: false },
            ]}
            items={dragArrayData || []}
            draggable
            droppableId="data"
            onDragEnd={handleDragEndData}
            getItemKey={(item) => item.ID}
            searchAccessor={(item) => item?.Title || ''}
            searchPlaceholder="Zoeken op titel"
            emptyMessage="Geen gegevens gevonden."
            addRow={canEdit ? {
              toolTipContent: 'Gegeven toevoegen',
              onClick: addData,
            } : undefined}
            renderRow={(item) => {
              const isStandardized = STANDARDIZED_DATA_FIELDS.includes(item.Title)
              return (
                <>
                  <TableDragHandleCell>
                    <DragIndicatorOutlinedIcon />
                  </TableDragHandleCell>
                  <TableCell>
                    {/* De titel wordt in de kolom afgekapt; de tooltip is de manier
                        om hem alsnog te lezen. De vertraging houdt het rustig in een
                        tabel waar elke rij er een heeft.
                        LET OP: de tooltip kent alleen muis-handlers, dus op een
                        telefoon verschijnt hij niet. Dat mag hier: de titel staat
                        in een invoerveld dat je kunt aantikken en doorschuiven,
                        dus hij is niet de enige drager van de tekst.
                        AI-CONTEXT: See docs/pitfalls.md#ui-hover-only-affordance-dies-on-mobile */}
                    <Tooltip content={item.Title || ''} placeholder="Nog geen titel" delay={DATA_TITLE_TOOLTIP_DELAY}>
                      {isStandardized ? (
                        <span>{item.Title}</span>
                      ) : (
                        <input
                          type="text"
                          defaultValue={item.Title}
                          data-docid={item.docid}
                          onBlur={titleHandler}
                          list="form-field-item-titles"
                          placeholder="Kies of typ een titel"
                          autoComplete="off"
                        />
                      )}
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <DataFieldValue
                      item={item}
                      canEdit={canEdit}
                      options={optionsFor(item)}
                      onChange={valueHandler}
                      onUpload={uploadHandler}
                    />
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <EditOutlinedIcon
                        className='table-delete-icon'
                        onClick={() => setEditDocid(item.docid)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <DeleteOutlinedIcon
                        className='table-delete-icon'
                        data-docid={item.docid}
                        onClick={isStandardized ? openDeleteModal : deleteNAW}
                      />
                    )}
                  </TableCell>
                </>
              )
            }}
          />
        )}
      </ReadOnlyGuard>
      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteItem={deleteStandardizedField}
        deleteName="dit gestandaardiseerd veld"
        softDelete
      />
      {/* Soort en opties worden meteen weggeschreven, net als de rest van deze
          tabel, dus een Opslaan-knop zou hier niets doen — en een knop die niets
          doet is erger dan geen knop.
          AI-CONTEXT: See docs/pitfalls.md#ui-removed-prop-resurrects-default */}
      <Modal
        modalOpen={!!editItem}
        setModalOpen={() => setEditDocid('')}
        pageHeader={`Soort en antwoordopties${editItem?.Title ? `: ${editItem.Title}` : ''}`}
        hideConfirm
        cancelLabel="Sluiten"
        content={
          <DataFieldTypeAndOptions
            type={resolveDataFieldType(editItem)}
            setType={fieldTypeHandler}
            dataOptions={optionsFor(editItem)}
            addOption={addOption}
            onChangeOption={changeOption}
            onDeleteOption={deleteOption}
            onReorderOptions={reorderOptions}
          />
        }
      />
    </>
  )
}

export default HouseholdDataTable
